/* eslint-disable camelcase */
import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import type { UseCanteenMenuState_UserDataFragmentFragment } from '@/generated/gql/graphql'
import type { ApolloError } from '@apollo/client'
import { useLazyQuery } from '@apollo/client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useInterval } from 'react-use'

import { parseTimeslotUrlDate } from '../timeslotDates'
import { getValidDateAndMeal as resolveValidDateAndMeal } from './getValidDateAndMeal'
import { getValidTimeslot } from './getValidTimeslot'
import { getMenuTimeRemaining } from './menuCountdown'
import { serializeMenuQueryDate } from './menuQueryDates'

const useCcMenuUserDataFragment = graphql(`
  fragment UseCanteenMenuState_UserDataFragment on User {
    accountLocation {
      id
      timezone
    }
    upcomingCcMenus {
      date
      breakfast {
        menuIsPublished
        timeslots {
          id
          endTime
        }
      }
      lunch {
        menuIsPublished
        timeslots {
          id
          endTime
        }
      }
      dinner {
        menuIsPublished
        timeslots {
          id
          endTime
        }
      }
    }
  }
`)

const useCcMenuCurrentMenuQuery = graphql(`
  query getMyMenu($meal: Meal!, $date: DateTime!) {
    me {
      id
      ccMenu(input: { meal: $meal, date: $date }) {
        id
        mealServiceData {
          ...CanteenMenu_CurrentMenuDataFragment
          id
          meal
          menuIsPublished
          openTime
          closeTime
          availableSubsidy
          timeslots {
            id
            vendorMenus {
              id
              vendor {
                currency
              }
            }
            id
            startTime
            endTime
            ordersOpenTime
            ordersCloseTime
          }
        }
      }
    }
  }
`)

export enum CCMenuStateCustomErrorCode {
  NoData = 'NO_MENU_DATA',
  InvalidDate = 'INVALID_MENU_DATE',
}

type CCMenuStateCustomError = {
  message: string
  name: CCMenuStateCustomErrorCode
}

type Props = {
  paramsDate: string
  paramsMeal: string
  paramsTimeslot: string
  userData?: FragmentType<typeof useCcMenuUserDataFragment> | null
}

export const useCcMenuState = ({
  paramsDate,
  paramsMeal,
  paramsTimeslot,
  userData: userDataProp,
}: Props) => {
  const userData = useFragment(useCcMenuUserDataFragment, userDataProp)

  const [now, setNow] = useState(() => Date.now())

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ApolloError | CCMenuStateCustomError | null>(null)
  const [validDate, setValidDate] = useState<string | null>(null)
  const [validMeal, setValidMeal] = useState<string | null>(null)
  const [validTimeslot, setValidTimeslot] = useState<string | null>(null)

  const paramsTimeslotISOString = parseTimeslotUrlDate(
    paramsDate,
    paramsTimeslot,
    userData?.accountLocation?.timezone,
  )

  const [
    runGetMenuDataQuery,
    {
      data: menuData,
      error: graphqlError,
      previousData: previousMenuData,
      refetch,
      loading: loadingMenuData,
      called: menuDataCalled,
    },
  ] = useLazyQuery(useCcMenuCurrentMenuQuery)

  const getValidDateAndMeal = useCallback(
    (
      dateToCheck: string,
      mealToCheck: string,
      userData: UseCanteenMenuState_UserDataFragmentFragment,
    ) =>
      resolveValidDateAndMeal(dateToCheck, mealToCheck, userData.upcomingCcMenus, {
        timezone: userData.accountLocation?.timezone,
      }),
    [],
  )

  useEffect(() => {
    if (!userData) return
    const { meal, date } = getValidDateAndMeal(paramsDate, paramsMeal, userData)
    setValidDate(date)
    setValidMeal(meal)
    const timezone = userData.accountLocation?.timezone
    const dateAsISOString = serializeMenuQueryDate(date, timezone)
    if (dateAsISOString === null) {
      setError({ name: CCMenuStateCustomErrorCode.InvalidDate, message: 'Invalid menu date' })
      setValidTimeslot(null)
      setLoading(false)
      return
    }
    setError(null)
    runGetMenuDataQuery({
      variables: { date: dateAsISOString, meal },
      fetchPolicy: 'cache-and-network',
    })
  }, [runGetMenuDataQuery, paramsDate, paramsMeal, getValidDateAndMeal, userData])

  useEffect(() => {
    if (!menuDataCalled || (!menuData && loadingMenuData)) return
    if (!menuData?.me.ccMenu?.mealServiceData.timeslots) return
    const validTimeslot = getValidTimeslot(
      paramsTimeslotISOString,
      menuData?.me.ccMenu?.mealServiceData.timeslots,
    )
    setValidTimeslot(validTimeslot ?? null)
    setLoading(false)
  }, [menuData, paramsTimeslotISOString, loadingMenuData, menuDataCalled])

  const closeTime = menuData?.me.ccMenu?.mealServiceData.closeTime

  // The cut-off is derived from the selected service and the clock, so it is
  // computed during render.
  const timezone = userData?.accountLocation?.timezone
  const orderTimeRemaining = useMemo(
    () => getMenuTimeRemaining(closeTime, now, timezone),
    [closeTime, now, timezone],
  )

  const menuClosed = Boolean(closeTime) && orderTimeRemaining < 0

  // Nothing to count down while the menu is closed or still loading
  useInterval(() => setNow(Date.now()), menuClosed || !closeTime ? null : 1000)

  const updateTimeslot = (timeslot: string) => {
    if (!menuData?.me.ccMenu?.mealServiceData.timeslots) return
    const validTimeslot = getValidTimeslot(timeslot, menuData.me.ccMenu.mealServiceData.timeslots)
    if (!validTimeslot) return
    setValidTimeslot(validTimeslot)
  }

  const updateSelectedService = (updateValues: Partial<{ date: string; meal: string }>) => {
    const dateToCheck = updateValues.date || validDate
    const mealToCheck = updateValues.meal || validMeal
    if (!dateToCheck || !mealToCheck || !userData) return
    const { meal, date } = getValidDateAndMeal(dateToCheck, mealToCheck, userData)
    if (meal === validMeal && date === validDate) return
    setLoading(true)
    setValidMeal(meal)
    setValidDate(date)
    setValidTimeslot(null)
  }

  return {
    loading: !error && (loading || loadingMenuData || !menuDataCalled),
    called: menuDataCalled,
    date: validDate,
    meal: validMeal,
    timeslot: validTimeslot,
    error: error ?? graphqlError,
    previousMenuData: previousMenuData?.me.ccMenu?.mealServiceData,
    menuData: menuData?.me.ccMenu?.mealServiceData,
    orderTimeRemaining,
    menuClosed,
    refetch,
    updateSelectedService,
    updateTimeslot,
  }
}
